import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Error404Page from './pages/authentication/404';

import SignIn from './pages/authentication/sign-in/SignIn';
import ForgotPassword from './pages/authentication/sign-in/Forgot';
import ForgotMFA from './pages/authentication/sign-in/MFAForgot';

// import SentMail from './pages/authentication/sentMail/SentMail';
import Frame from './components/Frame';

import Error403Page from './pages/authentication/403';
import Error500Page from './pages/authentication/500';
import Error503Page from './pages/authentication/503';
import SignInPage from './pages/authentication/sign-in';
import MembersPage from './pages/members';
import HolidayPage from './pages/tables/holidays';
import OnSitePage from './pages/tables/onSite';
import WfhPage from './pages/tables/wfh';
import WfhBalancesPage from './pages/tables/wfhBalances';
import VirtualizedTablePage from './pages/tables/virtualized';
import ThirdPartyPage from './pages/tables/thirdPartySite';
import ExceptionalWfh from './pages/tables/exceptionalWFH';
import ProfilesPage from './pages/forms/profiles';
import CalendarPage from './pages/calendar';
// import CombinedModesPage from './pages/tables/combinedModes/Index';
import LeavePage from './pages/tables/leave';
import WfhProfilesPage from './pages/wfhProfile/wfhProfile';

import LeaveRequestsPage from './pages/tables/leave/requests';
import LeaveBalancePage from './pages/tables/leave/balance';
import LeaveHistoryPage from './pages/tables/leave/history';
import UntracedUsersPage from './pages/tables/untracedUsers';
import LeaveTypesPage from './pages/tables/leave/leaveTypes';
import GlobalWorkPage from './pages/tables/globalWork';
import SupportPage from './pages/tables/support';
import SupportFlex from './pages/tables/support/FlexSupport';
import SupportTPS from './pages/tables/support/TpsSupport';
import SupportHome from './pages/tables/support/HomeSupport';
import SupportLeave from './pages/tables/support/LeaveSupport';
import SupportOther from './pages/tables/support/OtherSupport';
import DepartmentPage from './pages/forms/department';
import GradePage from './pages/forms/grade';
import WorkSpacesPage from './pages/office/workSpaces';
import FloorPage from './pages/office/workSpaces/floor';
// import HomePage from './pages/Home';

import setupInterceptors from './config/setupInterceptors';
// import store from './store';
import { Provider } from 'react-redux';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { persistor, store } from './config/store';
import { CustomProvider } from 'rsuite';
import { IntlProvider } from 'react-intl';
import enGB from 'rsuite/locales/en_GB';
import fr from 'rsuite/locales/fr_FR';
import locales from './locales';
import HomePage from './pages/Home';
import { fetchFeatures } from '@/services/feature.service';
import User from './pages/members/User';
import CombinedModesPage from './pages/tables/combinedModes/Index';
import FloorMaps from './pages/floorMaps';
import ProfilesAccess from './pages/ProfilesAccess/ProfilesAccess';
import IndividualAccess from './pages/individualAccess/IndividualAccess';
import WorkSpaces from './pages/workspaces';
import MembersSchedule from './pages/membersSchedule';
import Welcome from './pages/Home/Welcome';
// import Welcome from './pages/Home/Welcome copy';

import MyProfile from './pages/MyProfile';
import { jwtDecode } from 'jwt-decode';
import { getCurrentUser, setRefreshToken, setToken } from './services/auth.service';
import ReportingDashboard from './pages/reportingDashboard';
import TravelDashboard from './pages/reportingDashboard/travel';
import Dashboard from './pages/dashboard/index';
import DashboardSpaces from './pages/dashboard/dashboardSpaces';
import ResourcesSchedule from './pages/ressourcesSchedule';
import { useTranslation } from 'react-i18next';
import ScheduleProfiles from './pages/scheduleProfiles';

interface Feature {
  id: number;
  name: string;
  sections: Section[];
}

const App = () => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();

  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language || 'en');

  const currentUser = getCurrentUser();

  // const [isAuthenticated] = React.useState(localStorage.getItem('accessToken'));

  // const store = useStore();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const features = useAppSelector(state => state.feature.data);

  const navigate = useNavigate();

  // const features = [];
  useEffect(() => {
    isAuthenticated && dispatch(fetchFeatures());
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);

    const token = url.searchParams.get('token');
    const refreshToken = url.searchParams.get('refreshToken');
    const moduleEncoded = url.searchParams.get('module');

    if (token) {
      try {
        const decodedToken = jwtDecode(token);

        if (!decodedToken.admin) {
          navigate('/unauthorized');
        } else {
          setToken(token);
          setRefreshToken(refreshToken);

          if (moduleEncoded) {
            const moduleData = JSON.parse(atob(moduleEncoded));
            localStorage.setItem('module', JSON.stringify(moduleData));
          }

          url.searchParams.delete('token');
          url.searchParams.delete('refreshToken');
          url.searchParams.delete('module');

          const newUrl =
            url.searchParams.toString().length > 0
              ? `${url.pathname}?${url.searchParams.toString()}`
              : url.pathname;

          window.history.replaceState(null, '', newUrl);
          window.location.reload();
          // navigate('/');
        }
      } catch (error) {
        console.log(error);
        // navigate('/unauthorized');
      }
    }
  }, [navigate]);

  useEffect(() => {
    const onLanguageChange = () => {
      setCurrentLanguage(i18n.language);
    };

    i18n.on('languageChanged', onLanguageChange);

    return () => {
      i18n.off('languageChanged', onLanguageChange);
    };
  }, [i18n]);

  return (
    <Provider store={store}>
      <IntlProvider locale="en" messages={locales.en}>
        <CustomProvider locale={currentLanguage == 'en' ? enGB : fr}>
          <Routes>
            <Route path="/signin" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/forgot-mfa" element={<ForgotMFA />} />

            {/* <Route path="validation-code" element={<MFAPage />} /> */}

            {/* <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="sentMail" element={<SentMail />} /> */}
            <Route path="/" element={<Navigate to="/signin" replace />} />

            {/* <Route path="/" element={<SignIn />} /> */}

            {isAuthenticated && (
              <>
                <Route
                  index
                  element={<Welcome features={features} setSelectedFeature={setSelectedFeature} />}
                />
                <Route
                  path="/"
                  element={
                    <Frame
                      navs={features}
                      selectedFeature={selectedFeature}
                      setSelectedFeature={setSelectedFeature}
                    />
                  }
                >
                  {/* <Route index element={<HomePage selectedFeature={selectedFeature} />} /> */}
                  <Route path="home" element={<HomePage selectedFeature={selectedFeature} />} />
                  {currentUser?.moduleAccess?.includes('organizationSettings') ? (
                    <Route path="membersList" element={<MembersPage />} />
                  ) : (
                    <Route path="membersList" element={<Error403Page />} />
                  )}

                  {currentUser?.moduleAccess?.includes('organizationSettings') ? (
                    <Route path="member/:id" element={<User />} />
                  ) : (
                    <Route path="member/:id" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('organizationSettings') ? (
                    <Route path="department" element={<DepartmentPage />} />
                  ) : (
                    <Route path="department" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('organizationSettings') ? (
                    <Route path="grade" element={<GradePage />} />
                  ) : (
                    <Route path="grade" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('organizationSettings') ? (
                    <Route path="members-schedule" element={<MembersSchedule />} />
                  ) : (
                    <Route path="members-schedule" element={<Error403Page />} />
                  )}
                  {/* {currentUser?.moduleAccess?.includes('organizationSettings') ? (
                    <Route path="table-support" element={<SupportPage />} />
                  ) : (
                    <Route path="table-support" element={<Error403Page />} />
                  )} */}
                  {currentUser?.moduleAccess?.includes('organizationSettings') ? (
                    <Route path="table-support-other" element={<SupportOther />} />
                  ) : (
                    <Route path="table-support-other" element={<Error403Page />} />
                  )}

                  {currentUser?.moduleAccess?.includes('flexOffice') ? (
                    <Route path="floor-maps" element={<FloorMaps />} />
                  ) : (
                    <Route path="floor-maps" element={<Error403Page />} />
                  )}

                  {currentUser?.moduleAccess?.includes('flexOffice') ? (
                    <Route path="table-onsite" element={<OnSitePage />} />
                  ) : (
                    <Route path="table-onsite" element={<Error403Page />} />
                  )}

                  {currentUser?.moduleAccess?.includes('flexOffice') ? (
                    <Route path="flex-support" element={<SupportFlex />} />
                  ) : (
                    <Route path="flex-support" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('flexOffice') ? (
                    <Route path="office-workSpaces" element={<WorkSpacesPage />} />
                  ) : (
                    <Route path="office-workSpaces" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('flexOffice') ? (
                    <Route path="office-workSpaces/:floorId" element={<FloorPage />} />
                  ) : (
                    <Route path="office-workSpaces/:floorId" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('flexOffice') ? (
                    <Route path="access-profiles" element={<ProfilesAccess />} />
                  ) : (
                    <Route path="access-profiles" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('flexOffice') ? (
                    <Route path="individual-access" element={<IndividualAccess />} />
                  ) : (
                    <Route path="individual-access" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('flexOffice') ? (
                    <Route path="workspaces" element={<WorkSpaces />} />
                  ) : (
                    <Route path="workspaces" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('flexOffice') ? (
                    <Route path="ressources-schedule" element={<ResourcesSchedule />} />
                  ) : (
                    <Route path="ressources-schedule" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('remoteWork') ? (
                    <Route path="table-wfh" element={<WfhPage />} />
                  ) : (
                    <Route path="table-wfh" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('remoteWork') ? (
                    <Route path="table-wfhBalances" element={<WfhBalancesPage />} />
                  ) : (
                    <Route path="table-wfhBalances" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('remoteWork') ? (
                    <Route path="exceptional-wfh" element={<ExceptionalWfh />} />
                  ) : (
                    <Route path="exceptional-wfh" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('remoteWork') ? (
                    <Route path="wfhProfiles" element={<WfhProfilesPage />} />
                  ) : (
                    <Route path="wfhProfiles" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('remoteWork') ? (
                    <Route path="table-thirdPartySite" element={<ThirdPartyPage />} />
                  ) : (
                    <Route path="table-thirdPartySite" element={<Error403Page />} />
                  )}
                  {/* {currentUser?.moduleAccess?.includes('remoteWork') ? (
                    <Route path="support-thirdPartySite" element={<SupportTPS />} />
                  ) : (
                    <Route path="support-thirdPartySite" element={<Error403Page />} />
                  )} */}
                  {currentUser?.moduleAccess?.includes('remoteWork') ? (
                    <Route path="table-supportWFH" element={<SupportHome />} />
                  ) : (
                    <Route path="table-supportWFH" element={<Error403Page />} />
                  )}

                  {currentUser?.moduleAccess?.includes('leave') ? (
                    <Route path="table-holidays" element={<HolidayPage />} />
                  ) : (
                    <Route path="table-holidays" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('leave') ? (
                    <Route path="leave-types" element={<LeaveTypesPage />} />
                  ) : (
                    <Route path="leave-types" element={<Error403Page />} />
                  )}

                  {currentUser?.moduleAccess?.includes('leave') ? (
                    <Route path="table-leave-requests" element={<LeaveRequestsPage />} />
                  ) : (
                    <Route path="table-leave-requests" element={<Error403Page />} />
                  )}

                  {currentUser?.moduleAccess?.includes('leave') ? (
                    <Route path="table-leave-supports" element={<SupportLeave />} />
                  ) : (
                    <Route path="table-leave-supports" element={<Error403Page />} />
                  )}

                  {currentUser?.moduleAccess?.includes('leave') ? (
                    <Route path="table-leave-balance" element={<LeaveBalancePage />} />
                  ) : (
                    <Route path="table-leave-balance" element={<Error403Page />} />
                  )}

                  {currentUser?.moduleAccess?.includes('leave') ? (
                    <Route path="table-leave-history" element={<LeaveHistoryPage />} />
                  ) : (
                    <Route path="table-leave-history" element={<Error403Page />} />
                  )}

                  {/* <Route path="table-virtualized" element={<VirtualizedTablePage />} /> */}

                  {currentUser?.moduleAccess?.includes('analytics') ? (
                    <Route path="table-untracedUsers" element={<UntracedUsersPage />} />
                  ) : (
                    <Route path="table-untracedUsers" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('analytics') ? (
                    <Route path="table-combinedModes" element={<CombinedModesPage />} />
                  ) : (
                    <Route path="table-combinedModes" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('analytics') ? (
                    <Route path="team-members" element={<Dashboard />} />
                  ) : (
                    <Route path="team-members" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('analytics') ? (
                    <Route path="/spaces" element={<DashboardSpaces />} />
                  ) : (
                    <Route path="/spaces" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('analytics') ? (
                    <Route path="global-workmodes" element={<GlobalWorkPage />} />
                  ) : (
                    <Route path="global-workmodes" element={<Error403Page />} />
                  )}
                  {currentUser?.moduleAccess?.includes('analytics') ? (
                    <Route path="dashboard-travel" element={<TravelDashboard />} />
                  ) : (
                    <Route path="dashboard-travel" element={<Error403Page />} />
                  )}

                  {currentUser?.moduleAccess?.includes('remoteWork') ? (
                    <Route path="wfh-planning" element={<ScheduleProfiles />} />
                  ) : (
                    <Route path="wfh-planning" element={<Error403Page />} />
                  )}

                  {/* <Route path="error-404" element={<Error404Page />} />
                  <Route path="error-403" element={<Error403Page />} />
                  <Route path="error-500" element={<Error500Page />} />
                  <Route path="error-503" element={<Error503Page />} /> */}
                  <Route path="sign-in" element={<SignInPage />} />
                  {/* <Route path="form-profiles" element={<ProfilesPage />} /> */}
                  {/* <Route path="table-leave" element={<LeavePage />} /> */}

                  <Route path="myProfile" element={<MyProfile />} />

                  {/* <Route path="report-dashboard" element={<ReportingDashboard />} /> */}

                  {/* <Route index element={<HomePage />} /> */}
                </Route>
                <Route path="*" element={<Error404Page />} />
              </>
            )}

            <Route path="*" element={<Error403Page />} />
          </Routes>
          {setupInterceptors(store)}
          {/* </IntlProvider> */}
          {/* </Provider> */}
        </CustomProvider>
      </IntlProvider>
    </Provider>
  );
};

export default App;
